import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import Chart from "react-apexcharts";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { ChartCard } from "../../componentes/chart/CardChart";
import TabelaEntriesGrafico from "../../components/solicitacoes/Dashboard/TabelaEntriesGrafico";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import SolicitacaoCompraService from "../../services/resource/solicitacaoCompraService";
import UsuarioService from "../../services/resource/usuarioService";
import FilialService from "../../services/resource/filialService";
import TipoProdutoService from "../../services/resource/tipoProdutoService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { processoInfo } from "../../utils/solicitacoes/processoRegistry";
import { formatarMoeda } from "../../utils/solicitacoes/formatters";
import { donutOptions, barOptions, CORES_GRAFICO } from "../../utils/solicitacoes/chartOptions";

const service = new SolicitacaoCompraService();
const usuarioService = new UsuarioService();
const filialService = new FilialService();
const tipoProdutoService = new TipoProdutoService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));
const PRIORIDADE_OPTIONS = [
    { value: "BAIXA", label: "Baixa" }, { value: "NORMAL", label: "Normal" },
    { value: "ALTA", label: "Alta" }, { value: "URGENTE", label: "Urgente" },
];
const ETAPA_OPTIONS = (processoInfo("SOLICITACAO_COMPRAS")?.etapas || []).map(e => ({ value: e.codigo, label: e.label }));

const FILTROS_INICIAIS_EXTRA = {
    filialId: null, prioridade: null, tipoProdutoId: null, produtoNome: "", veiculoPlaca: "",
    fornecedorNome: "", status: null, etapaAtual: null, solicitanteId: null, responsavelId: null,
    vencimentoInicial: "", vencimentoFinal: "",
};

function resumoProdutos(produtos) {
    if (!produtos || produtos.length === 0) return "-";
    const linhas = produtos.map(p => `${p.nome} (${p.quantidade ?? 0}${p.veiculoPlaca ? ` · ${p.veiculoPlaca}` : ""})`);
    const primeiras = linhas.slice(0, 2).join(", ");
    const resto = linhas.length > 2 ? ` +${linhas.length - 2}` : "";
    return (
        <span title={linhas.join("\n")} className="block max-w-[240px] truncate">
            {primeiras}{resto}
        </span>
    );
}

const COLUNAS = [
    { key: "numero", label: "Número", width: 120 },
    { key: "dataSolicitacao", label: "Data", format: "date" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "filialNome", label: "Filial" },
    { key: "prioridade", label: "Prioridade" },
    { key: "tipoProdutoNome", label: "Tipo de Produto" },
    { key: "produtos", label: "Produtos", sortable: false, filterable: false, render: resumoProdutos },
    { key: "fornecedorNome", label: "Fornecedor" },
    { key: "valor", label: "Valor", format: "currency" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "responsavelAtualNome", label: "Responsável" },
];

export default function DashboardSolicitacaoCompras() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [filiais, setFiliais] = useState([]);
    const [tiposProduto, setTiposProduto] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.dashboard.compras.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Dashboard de Solicitação de Compras";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        tipoProdutoService.listarAtivos().then(res => setTiposProduto(res.data)).catch(() => setTiposProduto([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    const porStatusEntries = Object.entries(dashboard?.porStatus || {}).map(([k, v]) => [STATUS_CONFIG[k]?.label || k, v]);
    const porEtapaEntries = Object.entries(dashboard?.porEtapa || {}).map(([k, v]) => [
        k === "FINALIZADO" ? "Finalizado" : (processoInfo("SOLICITACAO_COMPRAS")?.etapas || []).find(e => e.codigo === k)?.label || k, v,
    ]);
    const evolucaoEntries = Object.entries(dashboard?.evolucaoPeriodo || {});
    const porPrioridadeEntries = Object.entries(dashboard?.porPrioridade || {}).map(([k, v]) => [
        PRIORIDADE_OPTIONS.find(p => p.value === k)?.label || k, v,
    ]);
    const porTipoProdutoEntries = Object.entries(dashboard?.porTipoProduto || {});
    const porSolicitanteEntries = Object.entries(dashboard?.porSolicitante || {});
    const porFilialEntries = Object.entries(dashboard?.porFilial || {});
    const valorPorFilialEntries = Object.entries(dashboard?.valorPorFilial || {});
    const valorPorTipoProdutoEntries = Object.entries(dashboard?.valorPorTipoProduto || {});
    const porFornecedorEntries = Object.entries(dashboard?.porFornecedor || {});

    const opcoesBarraValor = {
        yaxis: { labels: { formatter: v => formatarMoeda(v) } },
        tooltip: { y: { formatter: v => formatarMoeda(v) } },
    };
    const opcoesBarraHorizontal = { plotOptions: { bar: { horizontal: true, borderRadius: 4 } } };

    const [viewStatus, setViewStatus] = useState("chart");
    const [viewEtapa, setViewEtapa] = useState("chart");
    const [viewEvolucao, setViewEvolucao] = useState("chart");
    const [viewPrioridade, setViewPrioridade] = useState("chart");
    const [viewTipoProduto, setViewTipoProduto] = useState("chart");
    const [viewSolicitante, setViewSolicitante] = useState("chart");
    const [viewFilial, setViewFilial] = useState("chart");
    const [viewValorFilial, setViewValorFilial] = useState("chart");
    const [viewValorTipoProduto, setViewValorTipoProduto] = useState("chart");
    const [viewFornecedor, setViewFornecedor] = useState("chart");

    return (
        <DashboardProcessoLayout
            compacto
            titulo="Dashboard de Solicitação de Compras"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={[
                { label: "Total de solicitações", value: dashboard?.total ?? 0, icon: "fa fa-shopping-cart", iconClassName: "bg-primary/10 text-primary", colspan: 'lg:col-span-2' },
                { label: "Pendentes", value: dashboard?.pendentes ?? 0, icon: "fa fa-clock", iconClassName: "bg-amber-100 text-amber-700", colspan: 'lg:col-span-2'} ,
                { label: "Finalizadas", value: dashboard?.finalizadas ?? 0, icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success", colspan: 'lg:col-span-2' },
                { label: "Valor Total", value: formatarMoeda(dashboard?.valorTotal ?? 0), icon: "fa fa-sack-dollar", iconClassName: "bg-blue-100 text-blue-700",  colspan: 'lg:col-span-2' },
            ]}
            graficos={(
                <>
                    <div className="lg:col-span-6">
                        <ChartCard title="Solicitações por status" view={viewStatus} onViewChange={setViewStatus} accent={CORES_GRAFICO[0]}
                            tableContent={<TabelaEntriesGrafico entries={porStatusEntries} colunaValor="Quantidade" />}>
                            {porStatusEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porStatusEntries.map(([, v]) => v)}
                                    options={donutOptions(porStatusEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title="Solicitações por etapa" view={viewEtapa} onViewChange={setViewEtapa} accent={CORES_GRAFICO[1]}
                            tableContent={<TabelaEntriesGrafico entries={porEtapaEntries} colunaValor="Quantidade" />}>
                            {porEtapaEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porEtapaEntries.map(([, v]) => v)}
                                    options={donutOptions(porEtapaEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-12">
                        <ChartCard title="Evolução no período" view={viewEvolucao} onViewChange={setViewEvolucao} accent={CORES_GRAFICO[2]}
                            tableContent={<TabelaEntriesGrafico entries={evolucaoEntries} colunaValor="Quantidade" />}>
                            {evolucaoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: evolucaoEntries.map(([, v]) => v) }]}
                                    options={barOptions(evolucaoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-6">
                        <ChartCard title="Solicitações por prioridade" view={viewPrioridade} onViewChange={setViewPrioridade} accent={CORES_GRAFICO[3]}
                            tableContent={<TabelaEntriesGrafico entries={porPrioridadeEntries} colunaValor="Quantidade" />}>
                            {porPrioridadeEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porPrioridadeEntries.map(([, v]) => v)}
                                    options={donutOptions(porPrioridadeEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title="Solicitações por tipo de produto" view={viewTipoProduto} onViewChange={setViewTipoProduto} accent={CORES_GRAFICO[4]}
                            tableContent={<TabelaEntriesGrafico entries={porTipoProdutoEntries} colunaValor="Quantidade" />}>
                            {porTipoProdutoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porTipoProdutoEntries.map(([, v]) => v)}
                                    options={donutOptions(porTipoProdutoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-6">
                        <ChartCard title={<>Maiores solicitantes<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por número de solicitações</span></>}
                            view={viewSolicitante} onViewChange={setViewSolicitante} accent={CORES_GRAFICO[5]}
                            tableContent={<TabelaEntriesGrafico entries={porSolicitanteEntries} colunaValor="Quantidade" />}>
                            {porSolicitanteEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: porSolicitanteEntries.map(([, v]) => v) }]}
                                    options={barOptions(porSolicitanteEntries.map(([k]) => k), opcoesBarraHorizontal)} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title={<>Maiores solicitações por filial<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por número de solicitações</span></>}
                            view={viewFilial} onViewChange={setViewFilial} accent={CORES_GRAFICO[6]}
                            tableContent={<TabelaEntriesGrafico entries={porFilialEntries} colunaValor="Quantidade" />}>
                            {porFilialEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: porFilialEntries.map(([, v]) => v) }]}
                                    options={barOptions(porFilialEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-6">
                        <ChartCard title={<>Valor de compra por filial<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por valor</span></>}
                            view={viewValorFilial} onViewChange={setViewValorFilial} accent={CORES_GRAFICO[0]}
                            tableContent={<TabelaEntriesGrafico entries={valorPorFilialEntries} colunaValor="Valor" formatarValor={formatarMoeda} />}>
                            {valorPorFilialEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Valor", data: valorPorFilialEntries.map(([, v]) => v) }]}
                                    options={barOptions(valorPorFilialEntries.map(([k]) => k), opcoesBarraValor)} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title={<>Valor de compra por tipo de produto<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por valor</span></>}
                            view={viewValorTipoProduto} onViewChange={setViewValorTipoProduto} accent={CORES_GRAFICO[1]}
                            tableContent={<TabelaEntriesGrafico entries={valorPorTipoProdutoEntries} colunaValor="Valor" formatarValor={formatarMoeda} />}>
                            {valorPorTipoProdutoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Valor", data: valorPorTipoProdutoEntries.map(([, v]) => v) }]}
                                    options={barOptions(valorPorTipoProdutoEntries.map(([k]) => k), opcoesBarraValor)} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-12">
                        <ChartCard title={<>Maiores fornecedores<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por número de solicitações</span></>}
                            view={viewFornecedor} onViewChange={setViewFornecedor} accent={CORES_GRAFICO[2]}
                            tableContent={<TabelaEntriesGrafico entries={porFornecedorEntries} colunaValor="Quantidade" />}>
                            {porFornecedorEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: porFornecedorEntries.map(([, v]) => v) }]}
                                    options={barOptions(porFornecedorEntries.map(([k]) => k), opcoesBarraHorizontal)} />
                            )}
                        </ChartCard>
                    </div>
                </>
            )}
            filtrosExtras={(
                <>
                    <Campo label="Filial">
                        <CustomDropdown value={filtros.filialId} onChange={e => setFiltro("filialId", e.value)} options={filiais}
                            optionValue="id" optionLabel="nome" placeholder="Todas as filiais" showClear filter />
                    </Campo>
                    <Campo label="Prioridade">
                        <CustomDropdown value={filtros.prioridade} onChange={e => setFiltro("prioridade", e.value)} options={PRIORIDADE_OPTIONS}
                            placeholder="Todas as prioridades" showClear />
                    </Campo>
                    <Campo label="Tipo de Produto">
                        <CustomDropdown value={filtros.tipoProdutoId} onChange={e => setFiltro("tipoProdutoId", e.value)} options={tiposProduto}
                            optionValue="id" optionLabel="nome" placeholder="Todos os tipos" showClear filter />
                    </Campo>
                    <Campo label="Produto">
                        <input type="text" value={filtros.produtoNome} onChange={e => setFiltro("produtoNome", e.target.value)}
                            placeholder="Buscar por nome" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Veículo (placa)">
                        <input type="text" value={filtros.veiculoPlaca} onChange={e => setFiltro("veiculoPlaca", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Fornecedor">
                        <input type="text" value={filtros.fornecedorNome} onChange={e => setFiltro("fornecedorNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Etapa atual">
                        <CustomDropdown value={filtros.etapaAtual} onChange={e => setFiltro("etapaAtual", e.value)} options={ETAPA_OPTIONS}
                            placeholder="Todas as etapas" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                    <Campo label="Responsável">
                        <CustomDropdown value={filtros.responsavelId} onChange={e => setFiltro("responsavelId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os responsáveis" showClear filter />
                    </Campo>
                    <Campo label="Vencimento de">
                        <input type="date" value={filtros.vencimentoInicial ?? ""} onChange={e => setFiltro("vencimentoInicial", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Vencimento até">
                        <input type="date" value={filtros.vencimentoFinal ?? ""} onChange={e => setFiltro("vencimentoFinal", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                className="text-[11px]"
                showHeader={false}
                exportFilename="dashboard_solicitacao_compras"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
